import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { fetchUsers, updateUserRole, deactivateUser } from '../services/analyticsService';
import { User as UserType } from '../types';
import { 
  User, 
  UserCheck, 
  UserX, 
  Shield, 
  Search,
  Filter,
  Download,
  Menu as MenuIcon,
  X as CloseIcon
} from 'lucide-react';
import ResponsiveSidebar from '../components/layout/ResponsiveSidebar';

const ManageCitizens: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser, isAdmin, loading: authLoading, signOut } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'citizen' | 'official' | 'admin'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/login');
    }
  };

  useEffect(() => {
    const loadUsers = async () => {
      if (authLoading) return;
      
      if (!isAdmin) {
        setLoading(false);
        return;
      }
      
      try {
        const usersData = await fetchUsers();
        if (usersData) {
          setUsers(usersData);
        }
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadUsers();
  }, [isAdmin, authLoading]);

  const handleUpdateUserRole = async (userId: string, role: 'citizen' | 'official' | 'admin') => {
    const success = await updateUserRole(userId, role);
    if (success) {
      setUsers(prev => 
        prev.map(u => 
          u.id === userId ? { ...u, role } : u
        )
      );
    }
  };

  const handleActivateUser = async (userId: string) => {
    const success = await updateUserRole(userId, 'citizen');
    if (success) {
      setUsers(prev => 
        prev.map(u => 
          u.id === userId ? { ...u, is_active: true } : u
        )
      );
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    const success = await deactivateUser(userId);
    if (success) {
      setUsers(prev => 
        prev.map(u => 
          u.id === userId ? { ...u, is_active: false } : u
        )
      );
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    const isActive = user.is_active !== false;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && isActive) || 
      (statusFilter === 'inactive' && !isActive);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const exportUsers = () => {
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Role', 'Department', 'Status', 'Created At'],
      ...filteredUsers.map(user => [
        user.full_name || 'N/A',
        user.email || 'N/A',
        user.phone || 'N/A',
        user.role,
        user.department || 'N/A',
        user.is_active !== false ? 'Active' : 'Inactive',
        user.created_at
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
          <p className="text-gray-500 mt-2">Current role: {currentUser?.role || 'Not authenticated'}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
            >
              {sidebarOpen ? <CloseIcon className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
            </button>
            <div className="flex items-center space-x-3">
              <img src="/hero.png" alt="Logo" className="w-8 h-8" />
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-800 whitespace-nowrap">Manage Citizens</h1>
                <p className="text-xs text-gray-600 hidden sm:block">Manage user accounts and permissions</p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={exportUsers}
              className="flex items-center space-x-1 px-2 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden xs:inline">Export</span>
            </button>
            <button
              onClick={handleSignOut}
              className="px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 transition-colors hidden sm:block"
            >
              {t('auth.logout')}
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        <ResponsiveSidebar 
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          userRole="official"
          currentPage="manage-citizens"
        />

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name or email..."
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value as any)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All Roles</option>
                  <option value="citizen">Citizen</option>
                  <option value="official">Official</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setRoleFilter('all');
                    setStatusFilter('all');
                  }}
                  className="w-full px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Mobile User Cards */}
          <div className="block sm:hidden space-y-3 mb-4">
            {filteredUsers.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
                <p className="text-gray-500 text-sm">No users found matching the current filters</p>
              </div>
            ) : (
              filteredUsers.map((userData) => (
                <div key={userData.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                  <div className="flex items-center mb-3">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {userData.full_name || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {userData.email || userData.phone || 'No contact info'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <p className="text-xs text-gray-500">Role</p>
                      <span className={`inline-flex px-2 py-1 text-xs leading-4 font-semibold rounded-full ${
                        userData.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        userData.role === 'official' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {userData.role}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      <span className={`inline-flex px-2 py-1 text-xs leading-4 font-semibold rounded-full ${
                        userData.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {userData.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Department</p>
                      <p className="text-xs text-gray-900">
                        {userData.role === 'official' || userData.role === 'admin' ? userData.department || 'N/A' : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="text-xs text-gray-900">
                        {new Date(userData.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <select
                      value={userData.role}
                      onChange={(e) => handleUpdateUserRole(userData.id, e.target.value as any)}
                      className="text-xs border border-gray-300 rounded px-2 py-1"
                      disabled={userData.id === currentUser?.id}
                    >
                      <option value="citizen">Citizen</option>
                      <option value="official">Official</option>
                      <option value="admin">Admin</option>
                    </select>
                    {userData.is_active !== false ? (
                      <button
                        onClick={() => handleDeactivateUser(userData.id)}
                        className="text-red-600 hover:text-red-900 flex items-center text-xs py-1"
                        disabled={userData.id === currentUser?.id}
                      >
                        <UserX className="w-3.5 h-3.5 mr-1" />
                        Deactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => handleActivateUser(userData.id)}
                        className="text-green-600 hover:text-green-900 flex items-center text-xs py-1"
                      >
                        <UserCheck className="w-3.5 h-3.5 mr-1" />
                        Activate
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop Users Table */}
          <div className="hidden sm:block bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800">User Accounts</h3>
              <div className="text-xs text-gray-500">
                Showing {filteredUsers.length} of {users.length} users
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th scope="col" className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th scope="col" className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th scope="col" className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th scope="col" className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-3 sm:px-6 sm:py-4 text-center text-gray-500 text-sm">
                        No users found matching the current filters
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((userData) => (
                      <tr key={userData.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <User className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600" />
                            </div>
                            <div className="ml-3">
                              <div className="text-xs sm:text-sm font-medium text-gray-900">
                                {userData.full_name || 'N/A'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {userData.email || userData.phone || 'No contact info'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            userData.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                            userData.role === 'official' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {userData.role}
                          </span>
                        </td>
                        <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap text-xs text-gray-500">
                          {userData.role === 'official' || userData.role === 'admin' ? userData.department || 'N/A' : '-'}
                        </td>
                        <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            userData.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userData.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap text-xs text-gray-500">
                          {new Date(userData.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap text-xs font-medium">
                          <div className="flex flex-col space-y-1">
                            <select
                              value={userData.role}
                              onChange={(e) => handleUpdateUserRole(userData.id, e.target.value as any)}
                              className="text-xs border border-gray-300 rounded px-2 py-1"
                              disabled={userData.id === currentUser?.id}
                            >
                              <option value="citizen">Citizen</option>
                              <option value="official">Official</option>
                              <option value="admin">Admin</option>
                            </select>
                            {userData.is_active !== false ? (
                              <button
                                onClick={() => handleDeactivateUser(userData.id)}
                                className="text-red-600 hover:text-red-900 flex items-center text-xs"
                                disabled={userData.id === currentUser?.id}
                              >
                                <UserX className="w-3.5 h-3.5 mr-1" />
                                Deactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => handleActivateUser(userData.id)}
                                className="text-green-600 hover:text-green-900 flex items-center text-xs"
                              >
                                <UserCheck className="w-3.5 h-3.5 mr-1" />
                                Activate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ManageCitizens;